'use client';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMotionValueEvent, useScroll, motion } from 'motion/react';
import { cn } from '@epihub/web/src/tailwind/fn/cn';

type StickyScrollRevealProps = {
  content: Array<{
    title: string;
    description: ReactNode;
    content?: React.ReactNode | any;
  }>;
  contentClassName?: string;
};

export const StickyScrollReveal = ({ content, contentClassName }: StickyScrollRevealProps) => {
  const [activeCard, setActiveCard] = React.useState(0);

  const ref = useRef<any>(null);
  const { scrollYProgress } = useScroll({
    // uncomment line 22 and comment line 23 if you DONT want the overflow container and want to have it change on the entire page scroll
    // target: ref
    container: ref,
    offset: ['start start', 'end start'],
  });

  const cardLength = content.length;

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    const cardsBreakpoints = content.map((_, index) => index / cardLength);
    const closestBreakpointIndex = cardsBreakpoints.reduce(
      (acc, breakpoint, index) => {
        const distance = Math.abs(latest - breakpoint);
        if (distance < Math.abs(latest - cardsBreakpoints[acc])) {
          return index;
        }
        return acc;
      },
      0,
    );
    setActiveCard(closestBreakpointIndex);
  });

  const backgroundColors = [
    '#0f172a', // slate-900
    '#000000', // black
    '#171717', // neutral-900
  ];

  const linearGradients = [
    'linear-gradient(to bottom right, #06b6d4, #10b981)', // cyan-500 to emerald-500
    'linear-gradient(to bottom right, #ec4899, #6366f1)', // pink-500 to indigo-500
    'linear-gradient(to bottom right, #f97316, #eab308)', // orange-500 to yellow-500
  ];

  const [backgroundGradient, setBackgroundGradient] = useState(
    linearGradients[0],
  );

  useEffect(() => {
    setBackgroundGradient(linearGradients[activeCard % linearGradients.length]);
  }, [activeCard]);

  return (
    <motion.div
      animate={{
        backgroundColor: backgroundColors[activeCard % backgroundColors.length],
      }}
      className='relative flex h-[30rem] justify-center space-x-0 overflow-y-auto p-4 sm:h-[40rem] sm:space-x-10 sm:p-10 lg:h-[50rem]'
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      ref={ref}
    >
      <div className='div relative flex items-start px-4'>
        <div className='max-w-2xl'>
          {content.map((item, index) => (
            <div key={item.title + index} className='my-10 sm:my-20'>
              <motion.h2
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: activeCard === index ? 1 : 0.3,
                }}
                className='text-2xl font-bold text-slate-100'
              >
                {item.title}
              </motion.h2>
              <motion.p
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: activeCard === index ? 1 : 0.3,
                }}
                className='text-kg mt-10 max-w-sm text-slate-300'
              >
                {item.description}
              </motion.p>
            </div>
          ))}
          <div className='h-40' />
        </div>
      </div>
      <div
        style={{ background: backgroundGradient }}
        className={cn(
          'sticky top-10 hidden h-60 w-80 overflow-hidden rounded-md bg-white lg:block',
          contentClassName,
        )}
      >
        {content[activeCard].content ?? null}
      </div>
    </motion.div>
  );
};
